import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
    Box,
    Card,
    CardBody,
    CardHeader,
    Heading,
    Spinner,
    Text,
    VStack,
    HStack,
    Image,
    Icon,
} from "@chakra-ui/react";
import { ChevronRightIcon, CalendarIcon } from "@chakra-ui/icons";
import { supabase } from "../utils/supabaseClient";
import { COLORS } from "../styles/colors";
import {
    groupPublicRunsByCourseType,
    parseOrgBranding,
    runDateLine,
    friendlyRpcError,
    normalizeCorporatePortalUen,
} from "./corporateEnrollHelpers";

/** Participant-only list: pick a run, then register at `/corporate-enroll/:token/:runCode/individual`. */
export function CorporateEnrollPortalPage() {
    const { token } = useParams();
    const [title, setTitle] = useState(null);
    const [clientCompanyName, setClientCompanyName] = useState(null);
    const [clientCompanyUen, setClientCompanyUen] = useState(null);
    const [organization, setOrganization] = useState({ name: null, logo_url: null });
    const [runs, setRuns] = useState([]);
    const [state, setState] = useState("loading");
    const [errorMessage, setErrorMessage] = useState(null);

    useEffect(() => {
        if (!token) {
            setState("missing");
            return;
        }
        let cancelled = false;
        (async () => {
            setState("loading");
            try {
                const { data, error } = await supabase.rpc("get_corporate_portal_public", {
                    p_token: token,
                });
                if (error) throw error;
                if (cancelled) return;
                if (!data?.portal) {
                    setState("missing");
                    return;
                }
                setTitle(data.portal.title);
                const portal = data.portal;
                setClientCompanyName(typeof portal.company_name === "string" ? portal.company_name : null);
                const uen = normalizeCorporatePortalUen(portal.company_uen);
                setClientCompanyUen(uen || null);
                setOrganization(parseOrgBranding(data.organization));
                setRuns(Array.isArray(data.runs) ? data.runs : []);
                setState("ready");
            } catch (e) {
                if (!cancelled) {
                    setErrorMessage(friendlyRpcError(e, "Could not load this link"));
                    setState("error");
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [token]);

    const grouped = useMemo(() => groupPublicRunsByCourseType(runs), [runs]);

    const runHref = (run) =>
        `/corporate-enroll/${token}/${encodeURIComponent(run.run_code)}/individual`;

    const shell = (inner) => (
        <Box minH="100vh" bgGradient="linear(to-b, gray.50, white)" py={{ base: 10, md: 14 }} px={4}>
            <Box maxW="container.md" mx="auto">
                {inner}
            </Box>
        </Box>
    );

    if (state === "loading") {
        return shell(
            <Card shadow="md" borderWidth="1px">
                <CardBody py={10} textAlign="center">
                    <Spinner size="lg" color={COLORS.shadowBlue} />
                    <Text mt={3} fontSize="sm" color="gray.600">
                        Loading…
                    </Text>
                </CardBody>
            </Card>,
        );
    }

    if (state === "error") {
        return shell(
            <Card shadow="md" borderWidth="1px" borderColor="red.200">
                <CardBody py={8}>
                    <Text fontSize="sm" color="red.600" textAlign="center">
                        {errorMessage}
                    </Text>
                </CardBody>
            </Card>,
        );
    }

    if (state === "missing" || !title) {
        return shell(
            <Card shadow="md" borderWidth="1px">
                <CardBody py={8}>
                    <Text fontSize="sm" color="gray.600" textAlign="center">
                        This enrollment link is invalid or no longer active.
                    </Text>
                </CardBody>
            </Card>,
        );
    }

    const { name: orgName, logo_url: logoUrl } = organization;

    return (
        <Box minH="100vh" bgGradient="linear(to-b, #F4EEEA, white)" py={{ base: 8, md: 14 }} px={4}>
            <Box maxW="container.md" mx="auto">
                <Card overflow="hidden" shadow="lg" borderWidth="1px" borderColor="blackAlpha.100">
                    <CardHeader
                        bg={COLORS.isabelline}
                        borderBottomWidth="1px"
                        borderColor="blackAlpha.100"
                        py={6}
                    >
                        <VStack align="stretch" spacing={3}>
                            <HStack spacing={4}>
                                {logoUrl ? (
                                    <Image
                                        src={logoUrl}
                                        alt={orgName || "Organisation"}
                                        maxH="48px"
                                        maxW="160px"
                                        objectFit="contain"
                                    />
                                ) : null}
                                <VStack align="start" spacing={0} flex={1}>
                                    {orgName ? (
                                        <Text fontSize="xs" fontWeight="600" color={COLORS.shadowBlue} textTransform="uppercase">
                                            {orgName}
                                        </Text>
                                    ) : null}
                                    <Heading size="md" color={COLORS.maastrichtBlue}>
                                        {title}
                                    </Heading>
                                    {clientCompanyName ? (
                                        <Text fontSize="md" color={COLORS.maastrichtBlue} fontWeight="bold" mt={1}>
                                            For {clientCompanyName}
                                            {clientCompanyUen ? (
                                                <Text as="span" fontWeight="semibold" color="gray.600" fontSize="sm">
                                                    {" "}
                                                    ({clientCompanyUen})
                                                </Text>
                                            ) : null}
                                        </Text>
                                    ) : null}
                                </VStack>
                            </HStack>
                            <Text fontSize="sm" color="gray.600">
                                Choose a course or certification intake, then complete the registration form for that run.
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                                Registering for several courses? Submit one application per run — after each submission,
                                return here and open the next course.
                            </Text>
                        </VStack>
                    </CardHeader>
                    <CardBody px={{ base: 5, md: 8 }} py={6}>
                        <VStack align="stretch" spacing={8}>
                            {Array.from(grouped.entries()).map(([typeName, list]) => (
                                <Box key={typeName} as="section">
                                    <Text
                                        fontSize="sm"
                                        fontWeight="semibold"
                                        color={COLORS.maastrichtBlue}
                                        mb={3}
                                    >
                                        {typeName}
                                    </Text>
                                    <VStack align="stretch" spacing={2}>
                                        {list.map((r) => (
                                            <Box
                                                key={`${r.run_kind}-${r.run_code}`}
                                                as={RouterLink}
                                                to={runHref(r)}
                                                display="block"
                                                borderWidth="1px"
                                                borderRadius="xl"
                                                borderColor="blackAlpha.100"
                                                p={4}
                                                bg="white"
                                                _hover={{
                                                    borderColor: COLORS.shadowBlue,
                                                    bg: "gray.50",
                                                }}
                                                transition="all 0.15s"
                                            >
                                                <HStack align="start" justify="space-between" spacing={3}>
                                                    <VStack align="start" spacing={2} flex={1} minW={0}>
                                                        <Text fontWeight="medium" fontSize="sm" color="gray.800">
                                                            {r.run_label}
                                                        </Text>
                                                        <HStack
                                                            flexWrap="wrap"
                                                            fontSize="xs"
                                                            color="gray.500"
                                                            spacing={2}
                                                        >
                                                            <HStack spacing={1}>
                                                                <Icon as={CalendarIcon} boxSize={3.5} />
                                                                <span>{runDateLine(r.start_date, r.end_date)}</span>
                                                            </HStack>
                                                            <Text as="span" display={{ base: "none", sm: "inline" }}>
                                                                ·
                                                            </Text>
                                                            <Text as="span">
                                                                {r.run_kind === "full_cert_run"
                                                                    ? "Certification intake"
                                                                    : "Course run"}
                                                            </Text>
                                                        </HStack>
                                                    </VStack>
                                                    <Icon as={ChevronRightIcon} boxSize={6} color="gray.400" mt={0.5} />
                                                </HStack>
                                            </Box>
                                        ))}
                                    </VStack>
                                </Box>
                            ))}
                        </VStack>
                    </CardBody>
                </Card>
            </Box>
        </Box>
    );
}
